/**
 * What a product INCLUDES and which ADD-ONS it offers — shared by Create
 * Tenant (inside the Engage row, CommercialConfigurator) and Edit Tenant
 * (ProductAddonsSection), so the two screens cannot describe Engage
 * differently.
 *
 * Owner product model, 27 Sep 2026:
 *
 *   Engage Core, ON whenever Engage is sold:  Forms, Prospect Capture
 *   Optional billable add-ons, OFF by default: Surveys, Landing Pages
 *
 * EVERYTHING SHOWN COMES FROM THE SERVER. The included features are the
 * row's `included_features` (catalogue: `ENTITLEMENTS.engage`); the add-ons
 * are catalogue rows whose `add_on_of` names their product; prices are the
 * default book's, worded by the server. Nothing here names a feature, prices
 * one or decides that Surveys needs Engage.
 */
import React from 'react';
import { basePricesHref } from '../commercial/basePricesLink';

/**
 * THE FIX FOR AN UNPRICED ITEM, beside the words that say it is unpriced.
 *
 * Owner, 5 Oct 2026: "No price set" is not a permanent product state. When
 * the default price book has no entry for an item, the server says so ("Not
 * in the default price book"), names the book, and hands over a `remedy`
 * naming the price-book item. This renders that remedy as a link to Base
 * Prices opened on the item — in a NEW TAB, so a half-filled Create Tenant
 * form or an open Edit Tenant dialog survives — plus "Check again" when the
 * caller can re-read the price afterwards.
 *
 * Renders nothing without a remedy: a priced row needs none, and a conflict
 * Base Prices cannot resolve gets no button that would change nothing.
 */
export function PriceRemedy({ remedy, onRecheck, rechecking, testId }) {
  if (!remedy || remedy.action !== 'set_base_price' || !remedy.item_code) return null;
  const book = remedy.book_name
    ? `${remedy.book_name}${remedy.book_code ? ` (${remedy.book_code})` : ''}`
    : remedy.book_code;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs"
          data-testid={testId}>
      {book && (
        <span className="text-muted-foreground" data-testid={testId && `${testId}-book`}>
          Default price book: {book}
        </span>
      )}
      <a href={basePricesHref(remedy.item_code)} target="_blank" rel="noopener noreferrer"
         className="text-primary underline underline-offset-2"
         data-testid={testId && `${testId}-link`}>
        {remedy.label || 'Add a price in Base Prices'}
      </a>
      {onRecheck && (
        <button type="button" onClick={onRecheck} disabled={rechecking}
                className="text-muted-foreground underline underline-offset-2 disabled:opacity-60"
                data-testid={testId && `${testId}-recheck`}>
          {rechecking ? 'Checking…' : 'Check again'}
        </button>
      )}
    </span>
  );
}

/** Read-only ticks. Checked and disabled: they are part of the product, not
 *  a choice, and there is nothing to switch off. */
export function IncludedFeatureTicks({ code, features, productLabel }) {
  if (!features || features.length === 0) return null;
  return (
    <div className="mt-2 pl-6" data-testid={`included-features-${code}`}>
      <p className="text-xs text-muted-foreground mb-1">
        Included with {productLabel || 'this product'}
      </p>
      <ul className="flex flex-wrap gap-3">
        {features.map((f) => (
          <li key={f.code}>
            <label className="flex items-center gap-1.5 text-xs text-foreground">
              <input type="checkbox" checked readOnly disabled aria-readonly="true"
                     data-testid={`included-feature-${code}-${f.code}`} />
              {f.label}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The default-book price of an add-on, as the server worded it. Unpriced
 *  reads the server's words ("Not in the default price book"), never 0. */
export function addonPriceText(price) {
  if (!price) return null;
  if (!price.priced) return price.display;
  return `${price.currency} ${Number(price.amount).toFixed(2)} ${price.billing_unit_label}`;
}
